import { useEffect, useRef, useState } from 'react';

import { apiOrigin } from '../../services/api';

const AVATAR_COLORS = [
  'linear-gradient(145deg, #5b9f8c, #236b58)',
  'linear-gradient(145deg, #6a8fd8, #2f4f9f)',
  'linear-gradient(145deg, #d88a6a, #9f3f2f)',
  'linear-gradient(145deg, #b58fd8, #6f3f9f)',
  'linear-gradient(145deg, #d8b56a, #9f7f2f)',
  'linear-gradient(145deg, #6ad8b5, #2f9f7f)',
  'linear-gradient(145deg, #d86a8f, #9f2f4f)',
  'linear-gradient(145deg, #8ad86a, #4f9f2f)',
];

const pickColor = (seed: string): string => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(index);
    hash |= 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
};

// Module-level cache: one entry per chatId.
// - 'found': has a photo URL (shared blob URL)
// - 'missing': 404 — no photo available, use initials
// - 'rate-limited': 429 — temporarily use initials, retry after backoff
type CacheResult =
  | { kind: 'found'; url: string }
  | { kind: 'missing'; until: number }
  | { kind: 'rate-limited'; until: number };

const avatarCache = new Map<string, CacheResult>();
const avatarInFlight = new Map<string, Promise<CacheResult>>();
const RATE_LIMIT_BACKOFF_MS = 60_000; // 1 minute
const MISSING_RETRY_MS = 5 * 60_000; // 5 minutes

/**
 * Fetch a profile photo for a chat, with module-level caching:
 * - Deduplicates concurrent requests (in-flight promise per chatId)
 * - Caches 404 as 'missing' (no retry for 5 min)
 * - Caches 429 as 'rate-limited' (no retry for 1 min)
 * - Caches success as 'found' (shared blob URL, never re-fetched)
 * Does NOT log expected 404/429 to console (they are benign).
 */
function fetchAvatar(chatId: string, signal: AbortSignal): Promise<CacheResult> {
  const cached = avatarCache.get(chatId);
  if (cached) {
    const now = Date.now();
    if (cached.kind === 'found') return Promise.resolve(cached);
    if (cached.kind === 'missing' && now < cached.until) return Promise.resolve(cached);
    if (cached.kind === 'rate-limited' && now < cached.until) return Promise.resolve(cached);
  }

  // Deduplicate concurrent requests
  const inFlight = avatarInFlight.get(chatId);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<CacheResult> => {
    try {
      const res = await fetch(`${apiOrigin}/api/whatsapp/profile-photo/${encodeURIComponent(chatId)}`, {
        credentials: 'include',
        signal,
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const result: CacheResult = { kind: 'found', url };
        avatarCache.set(chatId, result);
        return result;
      }
      if (res.status === 404) {
        const result: CacheResult = { kind: 'missing', until: Date.now() + MISSING_RETRY_MS };
        avatarCache.set(chatId, result);
        return result;
      }
      if (res.status === 429) {
        const result: CacheResult = { kind: 'rate-limited', until: Date.now() + RATE_LIMIT_BACKOFF_MS };
        avatarCache.set(chatId, result);
        return result;
      }
      // Unexpected status — treat as missing to avoid retry storm
      const result: CacheResult = { kind: 'missing', until: Date.now() + MISSING_RETRY_MS };
      avatarCache.set(chatId, result);
      return result;
    } catch {
      // Network error or aborted — don't cache, allow retry
      return { kind: 'missing', until: 0 };
    } finally {
      avatarInFlight.delete(chatId);
    }
  })();

  avatarInFlight.set(chatId, promise);
  return promise;
}

type AvatarProps = {
  chatId: string;
  title: string;
  size?: number;
  className?: string;
};

const Avatar = ({ chatId, title, size = 42, className }: AvatarProps) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const lastChatId = useRef<string>(chatId);

  useEffect(() => {
    if (lastChatId.current !== chatId) {
      lastChatId.current = chatId;
      setPhotoUrl(null);
      setFailed(false);
    }
    let cancelled = false;
    const controller = new AbortController();

    void fetchAvatar(chatId, controller.signal).then((entry) => {
      if (cancelled) return;
      if (entry.kind === 'found') {
        setPhotoUrl(entry.url);
        setFailed(false);
      } else {
        // 'missing' or 'rate-limited' — show initials, no console error
        setPhotoUrl(null);
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [chatId]);

  const initial = title.slice(0, 1).toLocaleUpperCase() || '?';
  const background = pickColor(chatId);

  if (photoUrl && !failed) {
    return (
      <span
        className={`contact-avatar avatar-photo ${className ?? ''}`}
        style={{ width: size, height: size, flex: `0 0 ${size}px` }}
        aria-hidden="true"
      >
        <img
          src={photoUrl}
          alt=""
          onError={() => {
            setFailed(true);
            setPhotoUrl(null);
          }}
        />
      </span>
    );
  }

  return (
    <span
      className={`contact-avatar ${className ?? ''}`}
      style={{ width: size, height: size, flex: `0 0 ${size}px`, background }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
};

export default Avatar;
